import dayjs from 'dayjs';
import sortBy from 'lodash/sortBy';
import * as React from 'react';
import AutoSizer from 'react-virtualized/dist/es/AutoSizer';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { IEurResult } from '../../../data/providers/reports/eurReportProvider';
import { ISize } from '../../../data/types';
import { Column } from '../../layout/Column';
import { Row } from '../../layout/Row';
import { Card } from '../../shared/card/Card';
import { GraphContainer } from '../../shared/graphs/GraphContainer';
import { legendFormatter } from '../../shared/graphs/legendFormatter';
import { EPdfMode, isPdf, shouldInvert } from '../../shared/pdf/Pdf';
import { colors } from '../colors';
import { EurTooltip } from './EurTooltip';

interface IProps {
  results: IEurResult[];
  pdfMode: EPdfMode;
}

interface IDataPoint {
  'CMJ Jump Height': number;
  'Squat Jump Height': number;
  EUR: number;
  Date: number;
  units: string;
}

interface IGraphData {
  labels: string[];
  data: IDataPoint[];
}

export const EurGraph = (props: IProps): React.ReactElement => {
  const data = React.useMemo((): IGraphData => {
    const labels: string[] = ['CMJ Jump Height', 'Squat Jump Height'];

    const mData = props.results.map((result: IEurResult): IDataPoint => {
      return {
        Date: result.timestamp,
        ['CMJ Jump Height']: result.cmj,
        ['Squat Jump Height']: result.squat,
        EUR: result.eur,
        units: result.units,
      };
    });

    const sortedData = sortBy(mData, (point: IDataPoint) => point.Date);

    return {
      labels,
      data: sortedData as IDataPoint[],
    };
  }, [JSON.stringify(props.results)]);

  const [maxJumpHeight, _] = React.useMemo((): number[] => {
    return props.results.reduce(
      (acc: number[], curr: IEurResult): number[] => {
        const high = curr.cmj > curr.squat ? curr.cmj : curr.squat;
        const jumpHeight = acc[0] > high ? acc[0] : high;
        const eur = acc[1] > curr.eur ? acc[1] : curr.eur;
        return [jumpHeight, eur];
      },
      [0, 0]
    );
  }, [JSON.stringify(props.results)]);

  return (
    <Row>
      <Card
        noTopRadius
        inverse={shouldInvert(props.pdfMode)}
        style={{
          padding: '0px',
          minHeight: 'var(--graph-min-height)',
          ...(isPdf(props.pdfMode) ? { overflow: 'visible', height: 'auto' } : {}),
        }}
      >
        <Column>
          <GraphContainer
            inverse={shouldInvert(props.pdfMode)}
            style={{ paddingBlockEnd: '0px', ...(isPdf(props.pdfMode) ? { height: 'auto' } : {}) }}
          >
            <AutoSizer>
              {(size: ISize): React.ReactElement => {
                return (
                  <ComposedChart
                    width={size.width}
                    height={size.height}
                    data={data.data}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid
                      vertical={false}
                      horizontal
                      stroke='#ccc'
                      strokeOpacity={0.25}
                    />

                    <XAxis
                      dataKey='Date'
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: 'var(--color-text)' }}
                      tickFormatter={tickFormatter}
                    />

                    <YAxis
                      orientation='right'
                      yAxisId='right'
                      domain={[0, maxJumpHeight]}
                      axisLine={false}
                      tickLine={false}
                      tickCount={10}
                      tick={{ fill: 'var(--color-text)' }}
                    />

                    <YAxis
                      yAxisId='left'
                      orientation='left'
                      dataKey={'EUR'}
                      domain={[0, 2]}
                      tickCount={10}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: 'var(--color-text)' }}
                    />

                    <Tooltip
                      cursor={false}
                      content={EurTooltip}
                      formatter={tickFormatter}
                    />

                    {data.labels.map(
                      (m: string, i: number): React.ReactElement => {
                        return (
                          <Bar
                            key={i}
                            dataKey={m}
                            fill={colors[i]}
                            yAxisId='right'
                          />
                        );
                      }
                    )}

                    <Line
                      dataKey={'EUR'}
                      yAxisId='left'
                      connectNulls
                      stroke={
                        props.pdfMode === EPdfMode.PRINT
                          ? 'var(--color-dark-text)'
                          : 'var(--color-text)'
                      }
                      strokeWidth={2}
                      color='var(--color-scatter-primary)'
                      dot={{
                        stroke: 'rgba(0,0,0, .85)',
                        strokeWidth: 2,
                        r: 6,
                        strokeDasharray: '',
                      }}
                      fill={
                        props.pdfMode === EPdfMode.PRINT
                          ? 'var(--color-scatter-primary-inverse)'
                          : 'var(--color-scatter-primary)'
                      }
                    />

                    <Legend
                      iconType='circle'
                      formatter={(value) =>
                        legendFormatter(value, props.pdfMode)
                      }
                    />
                  </ComposedChart>
                );
              }}
            </AutoSizer>
          </GraphContainer>
        </Column>
      </Card>
    </Row>
  );
};
const tickFormatter = (tick: number): string =>
  tick > 12875256000 ? dayjs.utc(Number(tick)).format('M/D') : tick.toString();
